/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/style-prop-object */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _intersection from 'lodash/intersection';

// actions
import { setSelectedResult, setAutocompleteResults, updateSearchQuery } from '../actions/search';

// components
import DropdownField from '../components/Dropdown/Dropdown';

// helpers
import AutocompleteUtils from '../lib/AutocompleteUtils';
import { pushSearchInteraction } from '../lib/AnalyticsUtils';

/**
 * @description Autocomplete container handles logic for Google Maps API Places Autocomplete
 *
 * @param {object} props - Component props
 * @param {boolean} props.mapLoaded - Lets the container know that the Map has loaded.
 * @param {editing} props.editing - If the user is editing their route
 * @param {string} props.keyPressed - The key pressed by the user
 *
 * @returns {<Dropdown />}
 */
class Dropdown extends Component {
  constructor(props) {
    super(props);

    this.TIMER = 800;
    this.timeout = null;

    this.state = {
      autocompleteMounted: false,
      selectedInput: null,
    };

    this.handleItemClick = this.handleItemClick.bind(this);
    this.handleSearchEnter = this.handleSearchEnter.bind(this);
    this.updateSearchInput = this.updateSearchInput.bind(this);
    this.handleDropdownClose = this.handleDropdownClose.bind(this);
  }

  componentWillReceiveProps() {
    const { keyPressed } = this.props;

    if (keyPressed === 'ArrowUp' || keyPressed === 'ArrowDown') {
      this.setState({
        selectedInput: AutocompleteUtils.highlightPlace(keyPressed),
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { autocompleteMounted } = this.state;

    const { mapLoaded, userInput, keyPressed, selectedSearchResult, focused } = this.props;

    if (mapLoaded && !autocompleteMounted) {
      this.initAutocompleteService();
    }

    if (
      userInput &&
      focused &&
      ((userInput !== prevProps.userInput && userInput !== selectedSearchResult) ||
        !prevProps.focused)
    ) {
      this.getPlacePredictions(userInput);
    }

    if (keyPressed === 'Enter' && keyPressed !== prevProps.keyPressed) {
      // If no input was selected by the user. Return the first autocomplete result.
      this.handleSearchEnter();
    }
  }

  /**
   * @description Displays Google Maps Autocomplete predictions below the input field
   *
   * @param {string} input - User input
   *
   * @returns {void}
   */
  getPlacePredictions(input) {
    const { google } = window;

    const { userInput } = this.props;

    const displaySuggestions = (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        return;
      }

      let filteredPredictions = [];

      predictions.forEach(prediction => {
        if (_intersection(AutocompleteUtils.FORBIDDEN_TYPES, prediction.types).length === 0) {
          filteredPredictions.push(prediction.description);
        }
      });

      if (userInput === '') {
        filteredPredictions = [];
      }

      this.props.dispatchSetAutocompleteResults(filteredPredictions);
    };

    if (this.timeout === null) {
      this.timeout = setTimeout(() => {
        this.AutocompleteService.getPlacePredictions(
          {
            input,
          },
          displaySuggestions
        );
        this.timeout = null;
      }, this.TIMER);
    }

    if (input.length >= 3) {
      clearTimeout(this.timeout);
      this.timeout = null;
      this.AutocompleteService.getPlacePredictions(
        {
          input,
        },
        displaySuggestions
      );
    }
  }

  async handleSearchEnter() {
    const selectedAddress = this.state.selectedInput
      ? this.state.selectedInput.innerText
      : this.props.searchResults[0];
    const result = await AutocompleteUtils.getLocationDetails(selectedAddress);
    this.updateSearchInput(result);
  }

  async handleItemClick(e) {
    this.setState({ selectedInput: e.currentTarget });
    const result = await AutocompleteUtils.getLocationDetails(e.currentTarget.innerText);
    this.updateSearchInput(result);
  }

  updateSearchInput(result) {
    const { dispatchSetSelectedResult, dispatchUpdateSearchQuery } = this.props;

    if (!result) return;

    dispatchSetSelectedResult(result);
    dispatchUpdateSearchQuery(result ? result.address : null);

    // Push analytics.
    const { city, zip, state, country } = result;

    pushSearchInteraction({
      filters: this.props.filters,
      city,
      zip,
      state,
      country,
    });

    // Close dropdown on selected search result.
    this.handleDropdownClose();
  }

  handleDropdownClose() {
    const { dispatchSetAutocompleteResults } = this.props;

    // Close and Reset dropdown
    dispatchSetAutocompleteResults([]);
    this.setState({ selectedInput: null });
  }

  initAutocompleteService() {
    const { google } = window;

    this.AutocompleteService = new google.maps.places.AutocompleteService();

    this.setState({ autocompleteMounted: true });
  }

  mountAutocompleteService() {
    this.setState(prevState => {
      return {
        ...prevState,
        autocompleteMounted: true,
      };
    });
  }

  render() {
    if (!this.props.focused) return null;

    return (
      <DropdownField
        id="autocomplete"
        style="autocomplete--dropdown"
        predictions={this.props.searchResults}
        handleItemClick={this.handleItemClick}
        selected={this.state.selectedInput}
      />
    );
  }
}

Dropdown.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.shape).isRequired,
  mapLoaded: PropTypes.object,
  dispatchSetSelectedResult: PropTypes.func.isRequired,
  dispatchSetAutocompleteResults: PropTypes.func.isRequired,
  dispatchUpdateSearchQuery: PropTypes.func.isRequired,
  userInput: PropTypes.string.isRequired,
  keyPressed: PropTypes.string.isRequired,
  searchResults: PropTypes.arrayOf(PropTypes.shape),
  // eslint-disable-next-line react/require-default-props
  selectedSearchResult: PropTypes.object,
  focused: PropTypes.bool.isRequired,
};

Dropdown.defaultProps = {
  mapLoaded: false,
  searchResults: [],
};

const mapStateToProps = state => {
  return {
    filters: state.locations.filters,
    mapLoaded: state.map.map,
    userInput: state.search.userInput,
    searchResults: state.search.searchResults,
    selectedSearchResult: state.search.selectedSearchResult,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatchSetSelectedResult: selectedSearchResult => {
      dispatch(setSelectedResult(selectedSearchResult));
    },
    dispatchSetAutocompleteResults: searchResults => {
      dispatch(setAutocompleteResults(searchResults));
    },
    dispatchUpdateSearchQuery: query => {
      dispatch(updateSearchQuery(query));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Dropdown);
